import { makeAutoObservable, reaction, runInAction } from "mobx";

import { StockistIssue } from "../../../Common/DTOs/StockistIssue";
import { issueAjaxController } from "../../../Common/Ajax/IssueAjaxController";
import { filteringController } from "./FilteringController";
import { userAccountController } from "../../../Common/Controllers/UserAccountController";

const SEARCH_PAGE_SIZE = 20;

class SearchOutletController {
  isSearching = false;
  isLoadingMore = false;
  isDebouncing = false;
  upcomingResults: StockistIssue[] = [];
  latestResults: StockistIssue[] = [];
  extrasResults: StockistIssue[] = [];
  upcomingOffset = 0;
  latestOffset = 0;
  extrasOffset = 0;
  limit = 20;
  latestPage = 1;
  hasMoreLatest = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => filteringController.searchValue,
      (query) => {
        this.handleQueryChanged(query);
      }
    );
  }

  get mergedUpcomingLatest() {
    return [
      ...this.upcomingResults,
      ...this.latestResults,
    ];
  }

  handleQueryChanged(query: string) {
    if (!query.trim()) {
      this.clearResults();
      runInAction(() => this.isDebouncing = false);
      return;
    }

    runInAction(() => this.isDebouncing = true);

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(() => {
      runInAction(() => this.isDebouncing = false);
      this.runGlobalSearch(query);
    }, 850);
  }

  clearResults() {
    runInAction(() => {
      this.isSearching = false;
      this.upcomingResults = [];
      this.latestResults = [];
      this.extrasResults = [];
      this.latestPage = 1;
      this.hasMoreLatest = false;
    });
  }

  async runGlobalSearch(query: string) {
    if (!query) return;

    this.isSearching = true;
    this.upcomingResults = [];
    this.latestResults = [];
    this.extrasResults = [];
    this.latestPage = 1;
    this.hasMoreLatest = false;

    try {
      const [upcoming, latest, extras] = await Promise.all([
        issueAjaxController.searchUpcomingIssues(query),
        issueAjaxController.search(query, 1, SEARCH_PAGE_SIZE),
        userAccountController.isLoggedIn ? issueAjaxController.searchIssuesForExtras(query) : Promise.resolve([]),
      ]);

      if (filteringController.searchValue !== query) return;

      runInAction(() => {
        this.upcomingResults = upcoming || [];
        this.latestResults = latest || [];
        this.extrasResults = extras || [];
        this.hasMoreLatest = (latest?.length ?? 0) === SEARCH_PAGE_SIZE;
        this.isSearching = false;
      });
    } catch (err) {
      if (err === "CANCELLED") return;
      runInAction(() => (this.isSearching = false));
    }
  }

  async loadMoreLatest() {
    const query = filteringController.searchValue;
    if (!query || this.isLoadingMore) return;

    this.isLoadingMore = true;
    const nextPage = this.latestPage + 1;

    try {
      const more = await issueAjaxController.search(query, nextPage, SEARCH_PAGE_SIZE);
      runInAction(() => {
        this.latestResults = [...this.latestResults, ...(more || [])];
        this.latestPage = nextPage;
        this.hasMoreLatest = (more?.length ?? 0) === SEARCH_PAGE_SIZE;
        this.isLoadingMore = false;
      });
    } catch (err) {
      if (err === "CANCELLED") return;
      runInAction(() => (this.isLoadingMore = false));
    }
  }
}

export const searchOutletController = new SearchOutletController();
